// EditCollectionTasks.jsx
import Timer from "otp-timer";
import React, { useEffect, useState, useContext, useRef } from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../../context/AuthContextProvider";
import { parse, format } from "date-fns";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { Box, Typography } from "@mui/material";
import Button from "@mui/material/Button";

import { Icon } from "@iconify/react";
import { set } from "lodash";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FormTextField from "../../components/Reusables/FormControls/FormTextFeilds";
import FormGenderSelect from "../../components/Reusables/FormControls/FormGenderSelection";
import FormCollectionDatePicker from "../../components/Reusables/FormControls/FormCollectionDatePicker";

import {
  useGetAssignees,
  searchPatient,
  updateCollection,
  useGetSymptoms,
  useGetCollectionDetails,
} from "../../api/collection";
import moment from "moment";
import { toast } from "sonner";
import * as Yup from "yup";
import { is } from "date-fns/locale";
import Loader from "../../components/Loader";

import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import FormField from "../../components/Reusables/FormControls/FormFeild";
import FormAssignToField from "../../components/Reusables/FormControls/FormAssignToField";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import { stripEmojis, withoutEmojis } from "../../utils/emoji";

const styles = { width: "100%", display: "block", marginBottom: 10 };

// Styles (kept for react-select if used elsewhere)
const customStyles = {
  control: (base) => ({
    ...base,
    width: "100%",
    height: "48px",
    border: "1px solid var(--app-border)",
    borderRadius: "8px",
    boxShadow: "none",
    cursor: "text",
    backgroundColor: "var(--app-surface)",
    padding: "0 8px",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: 4,
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    fontSize: "15px",
    color: "#fff5e8",
  }),
  singleValue: (provided) => ({
    ...provided,
    backgroundColor: "#fff5e8",
    borderRadius: "8px",
    padding: "4px 8px",
  }),
  placeholder: (base) => ({
    ...base,
    fontSize: "15px",
    color: "var(--app-text-muted)",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999999,
    borderRadius: "8px",
    width: "100%",
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 999999,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "15px",
    color: "var(--app-text-muted)",
    backgroundColor: state.isSelected
      ? "#F2F2F2"
      : state.isFocused
        ? "var(--app-surface-2)"
        : "white",
    cursor: "pointer",
    padding: "10px 12px",
    borderBottom: "1px solid var(--app-border)",
  }),
  multiValue: (styles) => ({
    ...styles,
    backgroundColor: "#fff5e8",
    borderRadius: "8px",
    padding: "2px 6px",
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    color: "var(--app-text)",
  }),
  multiValueRemove: (styles) => ({
    ...styles,
    color: "var(--app-text)",
    ":hover": {
      backgroundColor: "#f5d0a9",
      color: "var(--app-text)",
    },
  }),
};

function EditCollectionTasks() {
  const { user: authUser, open } = useContext(AuthContext);
  const [data, setData] = useState([]);
  const [createModal, setCreateModal] = useState(false);
  const [isUser, setIsUser] = useState({
    first_name: "",
    middle_name: "",
    last_name: "",
    uh_id: "",
    dateOfBirth: "",
    ref_no: "",
    email: "",
    abha_number: "",
    aadhaarNumber: "",
    phone: "",
    age: "",
    gender: "",
    id: 0,
    isCreate: true,
    label: "Create New Patient",
    value: 0,
    patientId: "",
  });
  const triggerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const [assignee, setAssignee] = useState(null);
  const [assigneeError, setAssigneeError] = useState(false);

  const [buttonLoading, setButtonLoading] = useState(false);
  const [bodyVitals, setBodyVitals] = useState({
    height: { value: null, unit: "cm", feet: "", inches: "" },
    weight: { value: null, unit: "kg" },
    temperature: { value: null, unit: "fahrenheit" },
    bloodPressure: { systolic: null, diastolic: null, unit: "mmHg" },
    spo2: { value: null, unit: "%" },
    bloodSugar: { value: null, unit: "mg/dL" }, // new
    respiratoryRate: { value: null, unit: "bpm" }, // new
    symptoms: [], // keep as array of string values
    notes: null,
  });

  const { collectionDetails, isLoading } = useGetCollectionDetails({ id: id });
  const { assignees } = useGetAssignees({
    centerId: collectionDetails?.data?.center_details?.id,
  });
  const { symptoms } = useGetSymptoms({ searchText: "" });

  // local option list for MUI Autocomplete (objects { label, value, description? })
  const [symptomOptions, setSymptomOptions] = useState([]);
  // selected option objects for Autocomplete
  const [selectedSymptomObjects, setSelectedSymptomObjects] = useState([]);
  const symptomsRef = useRef(null);

  useEffect(() => {
    setAssignee(collectionDetails?.data?.assign_to?._id);
  }, [assignees, collectionDetails]);

  const roundOrNull = (v) => {
    if (v === null || v === undefined) return null;
    const n = Number(v);
    if (Number.isNaN(n)) return null;
    return Math.round(n);
  };

  // When collectionDetails load, set user and bodyVitals
  useEffect(() => {
    if (!collectionDetails?.data) return;

    let userData = collectionDetails?.data?.user_details;
    setIsUser({
      first_name: userData?.firstName,
      middle_name: "",
      last_name: userData?.lastName,
      uh_id: userData?.uh_id || userData?.patientId,
      dateOfBirth: userData?.dateOfBirth
        ? parse(userData.dateOfBirth, "dd-MM-yyyy", new Date())
        : null,
      ref_no: collectionDetails.data?.secondary_reference_id,
      email: userData?.userEmail,
      abha_number: userData?.abha_number,
      adhaar_number: userData?.aadhaarNumber,
      phone: userData?.phone_no || userData?.phone_number,
      age: userData?.age,
      gender: userData?.gender,
      id: userData?._id,
      isCreate: false,
      patientId: userData?.patientId,
      abha_number: userData?.abhaDetails?.abhaNumber,
    });

    const data = {
      height: {
        value: roundOrNull(
          collectionDetails?.data?.body_vitals?.height?.value ?? null,
        ),
        unit: "cm",
        feet: "",
        inches: "",
      },
      weight: {
        value: roundOrNull(
          collectionDetails?.data?.body_vitals?.weight?.value ?? null,
        ),
        unit: "kg",
      },
      temperature: {
        value: collectionDetails?.data?.body_vitals?.temperature?.value ?? null,
        unit: "fahrenheit",
      },
      bloodPressure: {
        systolic:
          collectionDetails?.data?.body_vitals?.blood_pressure?.systolic ??
          null,
        diastolic:
          collectionDetails?.data?.body_vitals?.blood_pressure?.diastolic ??
          null,
        unit: "mmHg",
      },
      spo2: {
        value: collectionDetails?.data?.body_vitals?.spo2?.value ?? null,
        unit: "%",
      },
      bloodSugar: {
        value: collectionDetails?.data?.body_vitals?.blood_sugar?.value ?? null,
        unit:
          collectionDetails?.data?.body_vitals?.blood_sugar?.unit ?? "mg/dL",
      },
      respiratoryRate: {
        value:
          collectionDetails?.data?.body_vitals?.respiratory_rate?.value ?? null,
        unit:
          collectionDetails?.data?.body_vitals?.respiratory_rate?.unit ?? "bpm",
      },
      symptoms: collectionDetails?.data?.symptoms ?? [],
      notes: collectionDetails?.data?.medical_history_notes ?? null,
    };

    setBodyVitals(data);
  }, [collectionDetails]);

  // initialize symptomOptions when symptoms (from API) load
  useEffect(() => {
    if (!symptoms) return;
    // normalize incoming symptoms to { label, value, description? }
    const normalized = symptoms.map((s) => ({
      label: s.label ?? s.name ?? s.value ?? `${s}`,
      value: s.value ?? s.label ?? s.name ?? `${s}`,
      description: s.description,
    }));
    setSymptomOptions(normalized);
  }, [symptoms]);

  // when bodyVitals.symptoms or symptomOptions change, set selectedSymptomObjects for Autocomplete
  useEffect(() => {
    // bodyVitals.symptoms is array of strings (values)
    if (!Array.isArray(bodyVitals?.symptoms)) {
      setSelectedSymptomObjects([]);
      return;
    }
    const selected = bodyVitals.symptoms.map((val) => {
      const found = symptomOptions.find((o) => o.value === val);
      return found ? found : { label: val, value: val }; // create object for any custom string
    });
    setSelectedSymptomObjects(selected);
  }, [bodyVitals.symptoms, symptomOptions]);

  const userSchema = Yup.object().shape({
    first_name: Yup.string()
      .min(2, "Too Short!")
      .max(50, "Too Long!")
      .required("First name is required"),
    phone: Yup.string().required("Phone number is required"),
    gender: Yup.string().required("Gender is required"),
    dateOfBirth: Yup.date().required("Date of Birth is required"),
  });

  const [errors, setErrors] = useState({});

  const handleUpdateCollection = async () => {
    const addi = { ...bodyVitals };
    try {
      //  await userSchema.validate(isUser, { abortEarly: false });
      //   setErrors({});

      if (!assignee) {
        setAssigneeError(true);
        return;
      }

      if (addi.height.unit === "ft") {
        const feet = addi.height.feet || null;
        const inches = addi.height.inches || null;
        // Convert feet and inches to cm
        addi.height.value = feet * 30.48 + inches * 2.54;
      }

      if (addi.weight.unit === "lbs") {
        addi.weight.value = addi.weight.value * 0.453592;
      }

      // blood sugar conversion (normalize to mg/dL)
      if (addi.bloodSugar.unit === "mmol/L") {
        addi.bloodSugar.value = Number(addi.bloodSugar.value) * 18;
      }

      // Ensure numeric fields are numbers (or null)
      const toNumberOrNull = (v) => {
        if (v === null || v === undefined || v === "") return null;
        const n = Number(v);
        return Number.isNaN(n) ? null : n;
      };

      addi.bloodPressure.systolic = toNumberOrNull(addi.bloodPressure.systolic);
      addi.bloodPressure.diastolic = toNumberOrNull(
        addi.bloodPressure.diastolic,
      );
      addi.spo2.value = toNumberOrNull(addi.spo2.value);
      addi.temperature.value = toNumberOrNull(addi.temperature.value);
      addi.bloodSugar.value = toNumberOrNull(addi.bloodSugar?.value);
      addi.respiratoryRate.value = toNumberOrNull(addi.respiratoryRate?.value);
      addi.height.value = toNumberOrNull(addi.height.value);
      addi.weight.value = toNumberOrNull(addi.weight.value);

      const args = {
        patientPhoneNumber: isUser.phone,
        assignTo: assignee,
        patientId: isUser.patientId,
        medicalHistoryNotes: addi.notes,
        symptoms: addi.symptoms, // array of strings
        bodyVitals: {
          height: { value: addi.height.value, unit: "cm" },
          weight: { value: addi.weight.value, unit: "kg" },
          temperature: { value: addi.temperature.value, unit: "fahrenheit" },
          blood_pressure: {
            systolic: addi.bloodPressure.systolic,
            diastolic: addi.bloodPressure.diastolic,
            unit: "mmHg",
          },
          spo2: { value: addi.spo2.value, unit: "%" },
          blood_sugar: {
            value: addi.bloodSugar.value,
            unit: "mg/dL",
          }, // new
          respiratory_rate: {
            value: addi.respiratoryRate.value,
            unit: addi.respiratoryRate.unit,
          }, // new
        },
        timestamp: new Date().getTime(),
        patientDetails: {
          firstName: isUser.first_name,
          lastName: isUser.last_name,
          gender: isUser.gender,
          dateOfBirth: isUser.dateOfBirth,
          email: isUser.email,
          aadhaarNumber: isUser.aadhaarNumber,
          abhaNumber: isUser.abha_number,
        },
      };

      const collection_id = id;
      setButtonLoading(true);
      const response = await updateCollection(collection_id, args);
      if (response.success) {
        toast.success(response.message);
        navigate("/test-collections");
      } else {
        toast.error(response.message);
      }
    } catch (err) {
      const formattedErrors = {};
      if (err?.inner && Array.isArray(err.inner)) {
        err.inner.forEach((e) => {
          if (e.path) formattedErrors[e.path] = e.message;
        });
      }
      setErrors(formattedErrors);

      console.error("Error creating collection:", err);
    } finally {
      setButtonLoading(false);
    }
  };

  const formatOptionLabel2 = (option, { context }) => {
    if (context === "menu") {
      return (
        <div className="symptom-option">
          <div className="symptom-title">
            {option.label}
            {option.description && (
              <span className="symptom-desc"> ({option.description})</span>
            )}
          </div>
        </div>
      );
    }

    // Only show the label in the selected value (input)
    return option.label;
  };

  // Handler for MUI Autocomplete change
  const handleSymptomsAutocompleteChange = (event, newValue, reason) => {
    // newValue: array of either string (if freeSolo) or option objects
    // Normalize to array of option-objects: { label, value }
    const normalized = (newValue || [])
      .map((item) => {
        if (typeof item === "string") {
          // freeSolo typed string -> create object
          return { label: stripEmojis(item), value: stripEmojis(item) };
        } else if (item && item.inputValue) {
          // some patterns create inputValue
          const clean = stripEmojis(item.inputValue);
          return { label: clean, value: clean };
        } else if (typeof item === "object") {
          // existing option object
          return item;
        } else {
          return { label: `${item}`, value: `${item}` };
        }
      })
      // a symptom typed as nothing but emojis has no value left
      .filter((o) => String(o.value ?? "").trim() !== "");

    // Update local options: add any new custom ones so they show up in dropdown
    const newOptionsToAdd = normalized.filter(
      (o) => !symptomOptions.some((opt) => opt.value === o.value),
    );
    if (newOptionsToAdd.length) {
      setSymptomOptions((prev) => [...prev, ...newOptionsToAdd]);
    }

    setSelectedSymptomObjects(normalized);

    // Update bodyVitals.symptoms as array of string values
    setBodyVitals((prev) => ({
      ...prev,
      symptoms: normalized.map((o) => o.value),
    }));
  };

  return (
    <>
      <Helmet>
        <title>Spandan - Edit Collection Request</title>
      </Helmet>

      <div className="main-wrappers">
        <div
          className="page-wrapper justify-content-center align-items-center"
          style={{
            paddingBottom: "10px",
            paddingTop: "75px",
            maxHeight: "100vh",
            overflowY: "auto",
            backgroundColor: "var(--app-bg)",
            fontFamily: "Figtree",
          }}
        >
          <div className="content ">
            <h4
              onClick={() => {
                navigate(-1);
              }}
              style={{ cursor: "pointer" }}
            >
              <strong className="d-flex align-items-center">
                <Icon icon="weui:back-filled" width="12" height="24" />
                &nbsp; Edit Collection Request (#{id})
              </strong>
            </h4>

            {isLoading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: "100vh" }}
              >
                <Loader />
              </div>
            ) : (
              <div>
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    {" "}
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      fontSize={19}
                      color="var(--app-text)"
                    >
                      Patient Details
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <div className="row">
                      <div className="col-md-6"></div>
                    </div>

                    <>
                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <FormTextField
                              label="Mobile Number"
                              name="phone"
                              placeholder="Patient Mobile Number"
                              value={isUser.phone}
                              onChange={(e) =>
                                setIsUser((prev) => ({
                                  ...prev,
                                  phone: e.target.value,
                                }))
                              }
                              disabled={isUser.id !== 0}
                              errors={errors?.phone}
                            />
                            {errors.phone && (
                              <small className="text-danger">
                                {errors.phone}
                              </small>
                            )}
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="form-group">
                            <FormTextField
                              label="UHID"
                              name="uh_id"
                              placeholder="Patient UHID"
                              value={isUser.uh_id}
                              onChange={(e) =>
                                setIsUser((prev) => ({
                                  ...prev,
                                  uh_id: e.target.value,
                                }))
                              }
                              disabled={isUser.id !== 0}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <FormTextField
                              label="First Name"
                              name="first_name"
                              placeholder="First Name"
                              value={isUser.first_name}
                              onChange={(e) =>
                                setIsUser((prev) => ({
                                  ...prev,
                                  first_name: e.target.value,
                                }))
                              }
                              errors={errors?.first_name}
                              required
                              disabled={isUser.id == 0 ? false : true}
                            />
                            {errors.first_name && (
                              <small className="text-danger">
                                {errors.first_name}
                              </small>
                            )}
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="form-group">
                            <FormTextField
                              label="Last Name"
                              name="last_name"
                              placeholder="Last Name"
                              value={isUser.last_name}
                              onChange={(e) =>
                                setIsUser((prev) => ({
                                  ...prev,
                                  last_name: e.target.value,
                                }))
                              }
                              disabled={isUser.id !== 0}
                            />
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="form-group">
                            <FormGenderSelect
                              label="Gender"
                              required
                              value={isUser.gender}
                              onChange={(val) =>
                                setIsUser((prev) => ({
                                  ...prev,
                                  gender: val,
                                }))
                              }
                              errors={errors?.gender}
                              disabled={isUser.id !== 0}
                            />
                            {errors.gender && (
                              <small className="text-danger">
                                {errors.gender}
                              </small>
                            )}
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="form-group">
                            <FormCollectionDatePicker
                              label="Date of Birth"
                              value={isUser.dateOfBirth}
                              onChange={(val) =>
                                setIsUser((prev) => ({
                                  ...prev,
                                  dateOfBirth: val,
                                }))
                              }
                              errors={errors?.dateOfBirth}
                              disabled={isUser.id !== 0}
                            />
                            {errors.dateOfBirth && (
                              <small className="text-danger">
                                {errors.dateOfBirth}
                              </small>
                            )}
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="form-group">
                            <FormTextField
                              label="Reference No."
                              name="ref_no"
                              placeholder="Enter Patient Reference No."
                              value={isUser.ref_no}
                              onChange={(e) =>
                                setIsUser((prev) => ({
                                  ...prev,
                                  ref_no: e.target.value,
                                }))
                              }
                              disabled={isUser.id !== 0}
                            />
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="form-group">
                            <FormTextField
                              label="Email Address"
                              name="email"
                              placeholder="Enter Patient Email"
                              value={isUser.email}
                              onChange={(e) =>
                                setIsUser((prev) => ({
                                  ...prev,
                                  email: e.target.value,
                                }))
                              }
                              disabled={isUser.id !== 0}
                            />
                          </div>
                        </div>
                        {collectionDetails?.data?.center_details
                          ?.isAbhaVerified && (
                            <div className="col-md-4">
                              <div className="form-group">
                                {/* <label>ABHA Number</label>
                              <input
                                type="text"
                                onChange={(e) =>
                                  setIsUser((prev) => ({
                                    ...prev,
                                    abha_number: e.target.value,
                                  }))
                                }
                                placeholder="Enter ABHA Number"
                                value={isUser.abha_number}
                                className="form-control"
                                disabled={isUser.id == 0 ? false : true}
                              /> */}
                                <FormTextField
                                  label="ABHA Number"
                                  name="abha_number"
                                  placeholder="Enter ABHA Number"
                                  value={isUser.abha_number}
                                  onChange={(e) =>
                                    setIsUser((prev) => ({
                                      ...prev,
                                      abha_number: e.target.value,
                                    }))
                                  }
                                  disabled={isUser.id !== 0}
                                />
                              </div>
                            </div>
                          )}

                        {collectionDetails?.data?.center_details
                          ?.isAbhaVerified && (
                            <div className="col-md-4">
                              <div className="form-group">
                                {/* <label> Aadhaar Number</label>
                              <input
                                type="text"
                                onChange={(e) =>
                                  setIsUser((prev) => ({
                                    ...prev,
                                    adhaar_number: e.target.value,
                                  }))
                                }
                                placeholder="Enter Aadhaar Number"
                                value={isUser.adhaar_number}
                                className="form-control"
                                disabled={isUser.id == 0 ? false : true}
                              /> */}
                                <FormTextField
                                  label="Aadhaar Number"
                                  name="adhaar_number"
                                  placeholder="Enter Aadhaar Number"
                                  value={isUser.adhaar_number}
                                  onChange={(e) =>
                                    setIsUser((prev) => ({
                                      ...prev,
                                      adhaar_number: e.target.value,
                                    }))
                                  }
                                  disabled={isUser.id !== 0}
                                />
                              </div>
                            </div>
                          )}
                      </div>
                    </>
                  </AccordionDetails>
                </Accordion>

                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      fontSize={19}
                      color="var(--app-text)"
                    >
                      Additional Details{" "}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                      }}
                    >
                      {/* Height */}
                      <Box
                        sx={{
                          flex: { xs: "1 1 100%", md: "1 1 calc(50% - 16px)" },
                        }}
                      >
                        <FormField
                          label="Height"
                          type="height"
                          heightUnit={bodyVitals.height.unit}
                          value={bodyVitals.height.value}
                          feet={bodyVitals.height.feet}
                          inches={bodyVitals.height.inches}
                          onChange={(e) =>
                            setBodyVitals((prev) => ({
                              ...prev,
                              height: { ...prev.height, value: e.target.value },
                            }))
                          }
                          onFeetChange={(e) =>
                            setBodyVitals((prev) => ({
                              ...prev,
                              height: { ...prev.height, feet: e.target.value },
                            }))
                          }
                          onInchesChange={(e) =>
                            setBodyVitals((prev) => ({
                              ...prev,
                              height: {
                                ...prev.height,
                                inches: e.target.value,
                              },
                            }))
                          }
                          onHeightUnitChange={(unit) =>
                            setBodyVitals((prev) => ({
                              ...prev,
                              height: { ...prev.height, unit },
                            }))
                          }
                        />
                      </Box>

                      {/* Weight */}
                      <Box
                        sx={{
                          flex: { xs: "1 1 100%", md: "1 1 calc(50% - 16px)" },
                        }}
                      >
                        <FormField
                          label="Weight"
                          type="unitSelect"
                          placeholder={"Enter Weight"}
                          value={bodyVitals.weight.value}
                          unit={bodyVitals.weight.unit}
                          unitOptions={["kg", "lbs"]}
                          onChange={(e) =>
                            setBodyVitals((prev) => ({
                              ...prev,
                              weight: { ...prev.weight, value: e.target.value },
                            }))
                          }
                          onUnitChange={(unit) =>
                            setBodyVitals((prev) => ({
                              ...prev,
                              weight: { ...prev.weight, unit },
                            }))
                          }
                        />
                      </Box>

                      {/* Temperature */}
                      <Box
                        sx={{
                          flex: {
                            xs: "1 1 100%",
                            md: "1 1 calc(33.33% - 16px)",
                          },
                        }}
                      >
                        <FormField
                          label="Temperature"
                          type="unit"
                          placeholder={"Enter Temperature"}
                          value={bodyVitals.temperature.value}
                          unit="°F"
                          onChange={(e) =>
                            setBodyVitals((prev) => ({
                              ...prev,
                              temperature: { value: e.target.value },
                            }))
                          }
                        />
                      </Box>

                      {/* Blood Pressure */}
                      <Box
                        sx={{
                          flex: {
                            xs: "1 1 100%",
                            md: "1 1 calc(33.33% - 16px)",
                          },
                        }}
                      >
                        <FormField
                          label="Blood Pressure"
                          type="bp"
                          systolic={bodyVitals.bloodPressure.systolic}
                          diastolic={bodyVitals.bloodPressure.diastolic}
                          unit="mm Hg"
                          onChangeSys={(e) =>
                            setBodyVitals((prev) => ({
                              ...prev,
                              bloodPressure: {
                                ...prev.bloodPressure,
                                systolic: e.target.value,
                              },
                            }))
                          }
                          onChangeDia={(e) =>
                            setBodyVitals((prev) => ({
                              ...prev,
                              bloodPressure: {
                                ...prev.bloodPressure,
                                diastolic: e.target.value,
                              },
                            }))
                          }
                        />
                      </Box>

                      {/* SpO2 */}
                      <Box
                        sx={{
                          flex: {
                            xs: "1 1 100%",
                            md: "1 1 calc(33.33% - 16px)",
                          },
                        }}
                      >
                        <FormField
                          label="SpO2"
                          type="unit"
                          value={bodyVitals.spo2.value}
                          unit="%"
                          placeholder="Enter SpO2"
                          onChange={(e) =>
                            setBodyVitals((prev) => ({
                              ...prev,
                              spo2: { ...prev.spo2, value: e.target.value },
                            }))
                          }
                        />
                      </Box>

                      {/* Blood Sugar (NEW) */}
                      <Box
                        sx={{
                          flex: {
                            xs: "1 1 100%",
                            md: "1 1 calc(33.33% - 16px)",
                          },
                        }}
                      >
                        <FormField
                          label="Blood Sugar"
                          type="unitSelect"
                          value={bodyVitals.bloodSugar.value}
                          unit={bodyVitals.bloodSugar.unit}
                          unitOptions={["mg/dL", "mmol/L"]}
                          placeholder="Enter Blood Sugar"
                          onChange={(e) =>
                            setBodyVitals((prev) => ({
                              ...prev,
                              bloodSugar: {
                                ...prev.bloodSugar,
                                value: e.target.value,
                              },
                            }))
                          }
                          onUnitChange={(unit) =>
                            setBodyVitals((prev) => ({
                              ...prev,
                              bloodSugar: { ...prev.bloodSugar, unit },
                            }))
                          }
                        />
                      </Box>

                      {/* Respiratory Rate (NEW) */}
                      <Box
                        sx={{
                          flex: {
                            xs: "1 1 100%",
                            md: "1 1 calc(33.33% - 16px)",
                          },
                        }}
                      >
                        <FormField
                          label="Respiratory Rate"
                          type="unit"
                          value={bodyVitals.respiratoryRate.value}
                          unit={bodyVitals.respiratoryRate.unit}
                          placeholder="Enter Respiratory Rate"
                          onChange={(e) =>
                            setBodyVitals((prev) => ({
                              ...prev,
                              respiratoryRate: {
                                ...prev.respiratoryRate,
                                value: e.target.value,
                              },
                            }))
                          }
                        />
                      </Box>

                      {/* Symptoms */}
                      <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 100%" } }}>
                        <div className="form-group">
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 500,
                              color: "var(--app-text-muted)",
                              mb: "6px",
                              fontFamily: "Figtree",
                            }}
                          >
                            Symptoms
                          </Typography>
                          <div ref={triggerRef}>
                            <Autocomplete
                              multiple
                              freeSolo
                              options={symptomOptions}
                              value={selectedSymptomObjects}
                              onChange={handleSymptomsAutocompleteChange}
                              getOptionLabel={(option) =>
                                typeof option === "string"
                                  ? option
                                  : (option.label ?? option.value ?? "")
                              }
                              isOptionEqualToValue={(option, value) =>
                                (option?.value ?? option?.label) ===
                                (value?.value ?? value?.label)
                              }
                              filterSelectedOptions
                              renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                  <Chip
                                    label={option.label ?? option.value}
                                    {...getTagProps({ index })}
                                    key={option.value ?? option.label ?? index}
                                  />
                                ))
                              }
                              renderOption={(props, option) => {
                                return (
                                  <ListItem {...props} key={option.value}>
                                    <ListItemText
                                      primary={option.label}
                                      secondary={option.description || null}
                                    />
                                  </ListItem>
                                );
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder={
                                    selectedSymptomObjects?.length === 0
                                      ? "Select or type symptoms"
                                      : ""
                                  }
                                  variant="outlined"
                                  size="small"
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      transition: "none",

                                      "& .MuiOutlinedInput-notchedOutline": {
                                        border: "1px solid var(--app-border)",
                                      },

                                      "&:hover .MuiOutlinedInput-notchedOutline": {
                                        border: "1px solid var(--app-border)",
                                      },

                                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        border: "1px solid var(--app-border)",
                                      },

                                      "&:before": {
                                        display: "none",
                                      },

                                      "&:after": {
                                        display: "none",
                                      },

                                      boxShadow: "none",
                                    },
                                  }}
                                  InputProps={{
                                    ...params.InputProps,
                                    sx: {
                                      height: "52px",
                                      borderRadius: "8px",
                                      backgroundColor: "var(--app-surface)",
                                      color: "var(--app-text)",
                                      paddingRight: "10px",
                                      boxShadow: "none",
                                      transition: "none",

                                      "& input": {
                                        color: "var(--app-text)",
                                        fontWeight: 400,
                                      },

                                      "& input::placeholder": {
                                        color: "var(--app-text-muted)",
                                        opacity: 1,
                                      },

                                      "& .MuiOutlinedInput-notchedOutline": {
                                        border: "1px solid var(--app-border)",
                                      },

                                      "&:hover": {
                                        boxShadow: "none",
                                        backgroundColor: "var(--app-surface)",
                                      },

                                      "&:hover .MuiOutlinedInput-notchedOutline": {
                                        border: "1px solid var(--app-border)",
                                      },

                                      "&.Mui-focused": {
                                        boxShadow: "none",
                                        backgroundColor: "var(--app-surface)",
                                      },

                                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        border: "1px solid var(--app-border)",
                                      },
                                    },
                                  }}
                                  inputProps={{
                                    ...params.inputProps,
                                    // freeSolo: clean what is typed so a chip
                                    // can never carry an emoji
                                    onChange: withoutEmojis(
                                      params.inputProps.onChange,
                                    ),
                                    style: { fontWeight: 400 },
                                  }}
                                />
                              )}
                            />
                          </div>
                        </div>
                      </Box>

                      {/* Medical history notes */}
                      {/* <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 100%" } }}>
                        <div className="form-group">
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 500,
                              color: "var(--app-text-muted)",
                              mb: "6px",
                              fontFamily: "Figtree",
                            }}
                          >
                            Medical History Notes
                          </Typography>
                          <textarea
                            value={bodyVitals.notes || ""}
                            onChange={(e) =>
                              setBodyVitals((prev) => ({
                                ...prev,
                                notes: e.target.value,
                              }))
                            }
                            className="form-control"
                            rows="4"
                            placeholder="Enter Medical History Notes"
                            style={{
                              overflow: "hidden",
                              resize: "none",
                              borderRadius: 10,
                            }}
                          />
                        </div>
                      </Box> */}
                      <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 100%" } }}>

                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "var(--app-text-muted)",
                            mb: "6px",
                            fontFamily: "Figtree",
                          }}
                        >
                          Medical History Notes
                        </Typography>

                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          placeholder="Enter Medical History Notes"
                          value={bodyVitals.notes || ""}
                          onChange={withoutEmojis((e) =>
                            setBodyVitals((prev) => ({
                              ...prev,
                              notes: e.target.value,
                            })),
                          )}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              backgroundColor: "var(--app-surface)",
                              transition: "none",
                              boxShadow: "none",

                              "& textarea": {
                                color: "var(--app-text)",
                                fontSize: "14px",
                                fontFamily: "Figtree",
                              },

                              "& textarea::placeholder": {
                                color: "var(--app-text-muted)",
                                opacity: 1,
                              },

                              "& .MuiOutlinedInput-notchedOutline": {
                                border: "1px solid var(--app-border)",
                              },

                              "&:hover": {
                                backgroundColor: "var(--app-surface)",
                              },

                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                border: "1px solid var(--app-border)",
                              },

                              "&.Mui-focused": {
                                boxShadow: "none",
                              },

                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                border: "1px solid var(--app-border)",
                              },
                            },
                          }}
                        />

                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>

                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      fontSize={19}
                      color="var(--app-text)"
                    >
                      Request Details
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <FormAssignToField
                      assignees={assignees}
                      assignee={assignee}
                      setAssignee={setAssignee}
                      assigneeError={assigneeError}
                      setAssigneeError={setAssigneeError}
                      noSpacing
                    />
                  </AccordionDetails>
                </Accordion>

                <br />
                <br />
                <div
                  style={{
                    position: "fixed",
                    bottom: "0px",
                    padding: "10px 0",
                    right: "0",
                    left: "0",
                    width: "100%",
                    backgroundColor: "var(--app-surface-2)",

                    zIndex: 1000,
                    display: "flex",
                    justifyContent: open ? "flex-start" : "flex-start",
                  }}
                >
                  {buttonLoading ? (
                    <button
                      className="btn btn-added-primary mt-0"
                      disabled={true}
                      style={{
                        width: "200px",
                        marginLeft: open ? "20%" : "8%",
                      }}
                    >
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>{" "}
                      Creating Request ...
                    </button>
                  ) : (
                    <button
                      onClick={() => handleUpdateCollection()}
                      className="btn btn-added-primary mt-0"
                      disabled={isUser == null}
                      style={{
                        width: "200px",
                        marginLeft: open ? "20%" : "8%",
                      }}
                    >
                      Update Request
                    </button>
                  )}
                </div>
                {/* <Box
                  sx={{
                    position: "fixed",
                    bottom: 0,
                    right: 0,
                    left: 0,
                    width: "100%",
                    py: "10px",
                    backgroundColor: "var(--app-surface)",
                    boxShadow: "0 -4px 12px rgba(0,0,0,0.06)",
                    zIndex: 1000,
                    display: "flex",
                    justifyContent: "flex-start",
                  }}
                >
                  {buttonLoading ? (
                    <Button
                      variant="contained"
                      disabled
                      sx={{
                        width: "200px",
                        ml: open ? "20%" : "8%",
                      }}
                    >
                      <CircularProgress size={18} sx={{ mr: 1, color: "white" }} />
                      Creating Request ...
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleUpdateCollection}
                      disabled={isUser == null}
                      sx={{
                        width: "200px",
                        ml: open ? "40%" : "20%",
                      }}
                    >
                      Update Request
                    </Button>
                  )}
                </Box> */}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default EditCollectionTasks;
